import { useAuthStore } from './authStore';

/**
 * Rechte der Panel-Rollen (CUR-995) — an EINER Stelle, damit Navigation und
 * Routen-Schutz nicht auseinanderlaufen koennen.
 *
 * **Das hier ist Bequemlichkeit, keine Sicherheit.** Die Grenze zieht das
 * Backend (`requireAdminArea` in `roleGuard.plugin.ts`); es weist einen
 * ADMIN_VIEWER auch dann ab, wenn jemand hier vorbeikommt. Was diese Datei
 * leistet: dem Leser keine Knoepfe zeigen, die ohnehin nur einen 403
 * einbringen.
 */

export type AdminPanelRole = 'ADMIN' | 'ADMIN_VIEWER';

/**
 * Panel-Pfade, die die Nur-Lesen-Rolle sieht. Spiegelbild von
 * `ADMIN_VIEWER_AREAS` im Backend.
 *
 * Der Default ist geschlossen: `canViewPath` laesst nur durch, was hier steht.
 * Eine neue Seite ist damit erst einmal ADMIN-only — sichtbar wird sie durch
 * einen Eintrag hier, nicht durch Vergessen.
 */
export const VIEWER_PATHS = ['/', '/users', '/social', '/analytics', '/feedback', '/campaigns'] as const;

/** Darf `role` den Pfad oeffnen? Prefix-Match, `/` nur exakt. */
export function canViewPath(role: AdminPanelRole | undefined, pathname: string): boolean {
  if (role !== 'ADMIN_VIEWER') return true;
  return VIEWER_PATHS.some((p) =>
    p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(`${p}/`),
  );
}

export function useAdminRole(): AdminPanelRole | undefined {
  return useAuthStore((s) => s.user?.role);
}

/** True, solange die Sitzung nichts aendern darf. */
export function useIsReadOnly(): boolean {
  return useAuthStore((s) => s.user?.role) === 'ADMIN_VIEWER';
}

/** Umkehrung — liest sich an den Aufrufstellen besser als `!useIsReadOnly()`. */
export function useCanWrite(): boolean {
  return !useIsReadOnly();
}

export const ROLE_LABEL: Record<AdminPanelRole, string> = {
  ADMIN: 'Admin',
  ADMIN_VIEWER: 'Nur Lesen',
};
